import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useModal } from '@private/modals';
import { Form, Formik, FormikHelpers } from 'formik';

import FormikInput from 'components/Form/FormikInput';
import ModalContainer, { IModalContainerProps } from 'components/ModalContainer';

import { SavePresetMessage } from '../Sidebar/styled';
import { presetInitialValues, presetValidationSchema } from '../utils/helpers';
import { IPresetForm } from '../utils/types';

export interface IConfirmModalConfig extends IModalContainerProps {
  filtersCount: number;
  onConfirm: (
    values: IPresetForm,
    helpers: FormikHelpers<IPresetForm>,
    closeModal: () => void,
  ) => void;
}

export interface IConfirmModalProps extends IConfirmModalConfig {
  onClose: () => void;
}

const SaveModal = ({ filtersCount, onClose, onConfirm, ...rest }: IConfirmModalProps) => {
  const { t } = useTranslation();

  const onSubmit = React.useCallback(
    (values: IPresetForm, helpers: FormikHelpers<IPresetForm>) => {
      onConfirm(values, helpers, onClose);
    },
    [onClose, onConfirm],
  );

  return (
    <Formik
      initialValues={presetInitialValues}
      onSubmit={onSubmit}
      validationSchema={presetValidationSchema}
    >
      {({ isSubmitting }) => (
        <Form>
          <ModalContainer
            disableRightBtn={isSubmitting}
            leftBtnText={t('modals.cancel')}
            onDismiss={onClose}
            onLeftAction={onClose}
            rightBtnLoading={isSubmitting}
            rightBtnText={t('modals.save')}
            rightBtnType='submit'
            title={t('common.filters.preset.title')}
            {...rest}
          >
            <SavePresetMessage size='md' variant='regular'>
              <Trans
                i18nKey='common.filters.preset.message'
                t={t}
                values={{ filtersCount }}
              />
            </SavePresetMessage>
            <FormikInput
              autoFocus
              label={t('common.filters.preset.presetName')}
              name='name'
              placeholder={t('common.filters.preset.presetName')}
            />
          </ModalContainer>
        </Form>
      )}
    </Formik>
  );
};

type TUseConfirmModalResult = [(config: IConfirmModalConfig) => void, () => void];

export const useSavePresetModal = (): TUseConfirmModalResult => {
  const { closeModal, showModal } = useModal();

  const showSaveModal = React.useCallback(
    (config: IConfirmModalConfig) => {
      showModal(<SaveModal {...config} onClose={closeModal} />);
    },
    [closeModal, showModal],
  );

  return [showSaveModal, closeModal];
};
